import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    LinearProgress,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    School as SchoolIcon,
    AccessTime as TimeIcon,
    CheckCircle as CompletedIcon,
    RadioButtonUnchecked as PendingIcon,
    PlayArrow as PlayIcon,
    Lock as LockIcon,
    ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useTranslate, useNotify, useDataProvider } from 'react-admin';
import {
    learningPathService,
    LearningPath,
    LearningPathCourse,
    LearningPathEnrollment,
} from '../../services/learningPathService';

interface LearningPathDetailProps {
    pathId: number;
    onBack?: () => void;
    onCourseSelect?: (courseId: number) => void;
}

const LearningPathDetail: React.FC<LearningPathDetailProps> = ({
    pathId,
    onBack,
    onCourseSelect,
}) => {
    const translate = useTranslate();
    const notify = useNotify();
    const dataProvider = useDataProvider();

    const [path, setPath] = useState<LearningPath | null>(null);
    const [courses, setCourses] = useState<LearningPathCourse[]>([]);
    const [enrollment, setEnrollment] = useState<LearningPathEnrollment | null>(null);
    const [completedCourseIds, setCompletedCourseIds] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(true);
    const [enrolling, setEnrolling] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Wichtig: alle Hooks vor den early-returns (loading / error). Ein useState
    // unter einem early-return-Pfad reisst die Hook-Order zwischen Renders auf
    // und produziert React #310 ("rendered more hooks than during previous render").
    const rawMode: string = (path as any)?.enrollmentMode || 'self';
    const [effectiveMode, setEffectiveMode] = useState<string>(rawMode);

    useEffect(() => {
        loadData();
    }, [pathId]);

    useEffect(() => {
        if (!path) { return; }
        if (rawMode === 'self') { setEffectiveMode('self'); return; }
        const allowedDepts: number[] = (path as any)?.allowedDepartments || [];
        const allowedGroups: number[] = (path as any)?.allowedGroups || [];
        if (allowedDepts.length === 0 && allowedGroups.length === 0) { setEffectiveMode(rawMode); return; }
        const check = async () => {
            try {
                const token = localStorage.getItem('token');
                const ssoUrl = process.env.REACT_APP_SSO_URL || 'http://localhost:9000';
                const res = await fetch(`${ssoUrl}/api/me`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
                });
                if (!res.ok) return;
                const me = await res.json();
                const deptId = me.department?.id ? Number(me.department.id) : null;
                if (deptId && allowedDepts.includes(deptId)) { setEffectiveMode('self'); return; }
                const gIds = (me.userGroups || []).map((g: any) => Number(g.id)).filter(Boolean);
                if (gIds.some((id: number) => allowedGroups.includes(id))) { setEffectiveMode('self'); return; }
            } catch { /* degradiert sauber */ }
            setEffectiveMode(rawMode);
        };
        check();
    }, [path, rawMode]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);

            const [pathData, coursesData, enrollmentData] = await Promise.all([
                learningPathService.getLearningPath(pathId),
                learningPathService.getPathCourses(pathId),
                learningPathService.getEnrollmentForPath(pathId),
            ]);

            setPath(pathData);
            setCourses(coursesData);
            setEnrollment(enrollmentData);

            // Completion-Status pro Kurs aus den eigenen CourseEnrollments ziehen.
            // Der CompanyFilter-Subscriber filtert automatisch auf den eigenen User
            // + Mandanten. Wir holen nur die IDs der Kurse, die bereits im Pfad sind,
            // als Filter, damit die Liste klein bleibt.
            try {
                const courseIdsInPath = coursesData
                    .map((pc) => (typeof pc.course === 'object' ? pc.course.id : pc.course))
                    .filter((id): id is number => typeof id === 'number');
                if (courseIdsInPath.length > 0) {
                    const { data: myEnrollments } = await dataProvider.getList('course_enrollments', {
                        pagination: { page: 1, perPage: 200 },
                        sort: { field: 'id', order: 'DESC' },
                        filter: { status: 'completed' },
                    });
                    const completedIds = new Set<number>(
                        myEnrollments
                            .map((e: any) => (typeof e.course === 'object' ? e.course.id : e.course))
                            .filter((id: any): id is number => typeof id === 'number')
                            .filter((id: number) => courseIdsInPath.includes(id))
                    );
                    setCompletedCourseIds(completedIds);
                }
            } catch {
                // Wenn der Endpoint nicht erreichbar ist, fallen wir auf "nichts
                // als completed markieren" zurück — degradiert sauber, kein Crash.
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load learning path');
        } finally {
            setLoading(false);
        }
    };

    const handleEnroll = async () => {
        try {
            setEnrolling(true);
            const newEnrollment = await learningPathService.enrollInPath(pathId);
            setEnrollment(newEnrollment);
            if (newEnrollment.status === 'pending_approval') {
                notify('Ihre Einschreibungsanfrage wurde gesendet. Sie werden benachrichtigt, sobald sie genehmigt wird.', { type: 'info' });
            } else {
                notify(translate('learningPaths.enrollSuccess', { _: 'Erfolgreich eingeschrieben!' }), { type: 'success' });
            }
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Enrollment failed';
            if (msg.includes('not allowed')) {
                notify('Selbsteinschreibung ist für diesen Lernpfad nicht erlaubt.', { type: 'warning' });
            } else {
                notify(msg, { type: 'error' });
            }
        } finally {
            setEnrolling(false);
        }
    };

    const getDifficultyColor = (difficulty: string): 'success' | 'warning' | 'error' => {
        switch (difficulty) {
            case 'beginner':
                return 'success';
            case 'intermediate':
                return 'warning';
            case 'advanced':
                return 'error';
            default:
                return 'success';
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !path) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error || 'Learning path not found'}
            </Alert>
        );
    }

    const isPending = enrollment?.status === 'pending_approval';
    const isRejected = enrollment?.status === 'rejected';
    const isEnrolled = !!enrollment && !isPending && !isRejected;
    const isCompleted = enrollment?.status === 'completed';
    const progress = enrollment ? parseFloat(enrollment.progressPercentage) : 0;
    const enrollmentMode = effectiveMode;

    return (
        <Box>
            {/* Back button */}
            {onBack && (
                <Button
                    startIcon={<BackIcon />}
                    onClick={onBack}
                    sx={{ mb: 2 }}
                >
                    {translate('common.back', { _: 'Zurück' })}
                </Button>
            )}

            {/* Header */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {/* Thumbnail */}
                    <Box
                        sx={{
                            width: 200,
                            height: 150,
                            borderRadius: 2,
                            bgcolor: 'primary.light',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundImage: path.thumbnailUrl ? `url(${path.thumbnailUrl})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            flexShrink: 0,
                        }}
                    >
                        {!path.thumbnailUrl && (
                            <SchoolIcon sx={{ fontSize: 60, color: 'white', opacity: 0.8 }} />
                        )}
                    </Box>

                    {/* Info */}
                    <Box sx={{ flexGrow: 1, minWidth: 250 }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Chip
                                label={learningPathService.getDifficultyLabel(path.difficulty)}
                                size="small"
                                color={getDifficultyColor(path.difficulty)}
                            />
                            {isCompleted && (
                                <Chip
                                    icon={<CompletedIcon />}
                                    label={translate('learningPaths.completed', { _: 'Abgeschlossen' })}
                                    size="small"
                                    color="success"
                                />
                            )}
                        </Box>

                        <Typography variant="h4" component="h1" gutterBottom>
                            {path.title}
                        </Typography>

                        {path.description && (
                            <Typography variant="body1" color="text.secondary" paragraph>
                                {path.description}
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <SchoolIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                    {courses.length} {translate('learningPaths.courses', { _: 'Kurse' })}
                                </Typography>
                            </Box>
                            {path.estimatedDuration && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TimeIcon fontSize="small" color="action" />
                                    <Typography variant="body2" color="text.secondary">
                                        {learningPathService.formatDuration(path.estimatedDuration)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Progress */}
                        {isEnrolled && !isCompleted && (
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('learningPaths.yourProgress', { _: 'Dein Fortschritt' })}
                                    </Typography>
                                    <Typography variant="body2" color="primary" fontWeight="medium">
                                        {learningPathService.formatProgress(progress)}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{ height: 10, borderRadius: 5 }}
                                />
                            </Box>
                        )}

                        {/* Enrollment status / button */}
                        {isPending && (
                            <Chip label="Anfrage ausstehend" color="warning" />
                        )}
                        {isRejected && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label="Anfrage abgelehnt" color="error" />
                                <Button variant="outlined" size="small" onClick={handleEnroll} disabled={enrolling}>
                                    Erneut anfragen
                                </Button>
                            </Box>
                        )}
                        {!isEnrolled && !isPending && !isRejected && enrollmentMode === 'self' && (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleEnroll}
                                disabled={enrolling}
                                startIcon={enrolling ? <CircularProgress size={20} /> : undefined}
                            >
                                {translate('learningPaths.enrollNow', { _: 'Jetzt einschreiben' })}
                            </Button>
                        )}
                        {!isEnrolled && !isPending && !isRejected && enrollmentMode === 'request' && (
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={handleEnroll}
                                disabled={enrolling}
                                startIcon={enrolling ? <CircularProgress size={20} /> : undefined}
                            >
                                Einschreibung anfragen
                            </Button>
                        )}
                        {!isEnrolled && !isPending && !isRejected && enrollmentMode === 'none' && (
                            <Typography variant="body2" color="text.secondary">
                                Kontaktieren Sie Ihren Vorgesetzten für die Einschreibung.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* Course list */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {translate('learningPaths.courseList', { _: 'Kursübersicht' })}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {path.certificateMode === 'path' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="path-certificate-hint">
                        {translate('learningPaths.pathCertificateHint', { _: 'Für diesen Lernpfad gibt es ein gemeinsames Zertifikat nach Abschluss aller Kurse – die einzelnen Kurse stellen keines aus.' })}
                    </Typography>
                )}

                <List>
                    {courses.map((pathCourse, index) => {
                        const course = pathCourse.course;
                        const courseIdNum = typeof course === 'object' ? course.id : course;
                        const isCourseCompleted = typeof courseIdNum === 'number'
                            && completedCourseIds.has(courseIdNum);
                        const isLocked = !isEnrolled && index > 0;

                        return (
                            <ListItemButton
                                key={pathCourse.id}
                                disabled={isLocked}
                                onClick={() => !isLocked && onCourseSelect?.(course.id)}
                                sx={{
                                    borderRadius: 1,
                                    mb: 1,
                                    bgcolor: isCourseCompleted ? 'success.light' : 'transparent',
                                    '&:hover': {
                                        bgcolor: isCourseCompleted ? 'success.light' : 'action.hover',
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                    {isLocked ? (
                                        <LockIcon color="disabled" />
                                    ) : isCourseCompleted ? (
                                        <CompletedIcon color="success" />
                                    ) : (
                                        <PendingIcon color="action" />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body1">
                                                {index + 1}. {course.title}
                                            </Typography>
                                            {pathCourse.isRequired && (
                                                <Chip label={translate('learningPaths.required', { _: 'Pflicht' })} size="small" />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                                            {pathCourse.reason && (
                                                <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }} data-testid="path-course-reason">
                                                    {pathCourse.reason}
                                                </Typography>
                                            )}
                                            {course.estimatedDuration && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {learningPathService.formatDuration(course.estimatedDuration)}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary">
                                                {learningPathService.getDifficultyLabel(course.difficulty)}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                {isEnrolled && !isCourseCompleted && !isLocked && (
                                    <Button
                                        size="small"
                                        startIcon={<PlayIcon />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCourseSelect?.(course.id);
                                        }}
                                    >
                                        {translate('learningPaths.start', { _: 'Starten' })}
                                    </Button>
                                )}
                            </ListItemButton>
                        );
                    })}
                </List>
            </Paper>
        </Box>
    );
};

export default LearningPathDetail;
